import { debounce, isEqual } from 'lodash';
import type { ForwardedRef } from 'react';
import { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import useDisclosure from '@/core/hooks/useDisclosure';
import { useIsLg } from '@/core/hooks/useMediaQuery';
import useOnClickOutside from '@/core/hooks/useOnClickOutside';
import type { LocDomain } from '@/core/lib/new-architecture/domain/locs.domain';
import Store from '@/core/lib/new-architecture/store';
import type { NewInputProps } from '@/core/types/components';
import type { LocationForm } from '@/core/types/geo';

type UseGeoInputParams = NewInputProps<LocationForm | null> & {
  forwardedRef: ForwardedRef<HTMLInputElement>;
  useGeoInputType: 'search' | 'limited' | 'page';
  value: LocationForm | null;
  onClose?: (values?: LocationForm) => void;
};

const useGeoInput = (params: UseGeoInputParams) => {
  const { forwardedRef, useGeoInputType, onChange, onBlur, value, onClose: onCloseParam, ...rest } = params;

  const isLg = useIsLg();
  const inputRef = useRef<HTMLInputElement>(null);
  const divRef = useRef<HTMLDivElement>(null);

  const { isOpen, onOpen, onClose: closeDisclosure } = useDisclosure();

  const [isSearching, setIsSearching] = useState(false);

  const [selectedItem, setSelectedItem] = useState<LocDomain | null>(null);
  const [stagedItem, setStagedItem] = useState<LocDomain | null>(null);

  const [distance, setDistance] = useState(20);
  const onChangeDistance = (newDistance: number) => setDistance(newDistance);
  useEffect(() => {
    if (distance >= 0 && stagedItem && stagedItem?.getLocationFormDistance() !== distance) {
      setStagedItem(prev => {
        const clone = prev?.clone();
        clone?.setLocationFormDistance(distance);
        return clone ?? null;
      });
    }

    if (stagedItem && selectedItem && stagedItem.getLocationFormDistance() !== selectedItem.getLocationFormDistance()) {
      onChange?.(stagedItem.getLocationForm());
      onBlur?.(stagedItem.getLocationForm());
    }
  }, [distance, stagedItem]);

  const isStageAndSelectedEqual = useMemo(() => isEqual(stagedItem?.getLocationForm(), selectedItem?.getLocationForm()), [stagedItem, selectedItem, distance]);

  const { data: locDomain } = Store.locs.useDetail(value?.loc);
  const onClose = () => {
    onCloseParam?.(locDomain?.getLocationForm());
    closeDisclosure();
  };

  useEffect(() => {
    if (value && locDomain) {
      locDomain.setLocationFormDistance(value.distance);
      setDistance(value.distance ?? 0);
      setSelectedItem(locDomain);
      setStagedItem(locDomain);
      return;
    }
    setDistance(0);
    setSelectedItem(null);
    setStagedItem(null);
  }, [value, locDomain]);

  useImperativeHandle(forwardedRef, () => inputRef.current as HTMLInputElement);

  const [search, setSearch] = useState('');

  const { data: current } = Store.current.useCurrent();
  const { data: locs, isLoading } = Store.locs.useLoc({ search, range: useGeoInputType === 'limited' ? 'limited' : 'large' });

  const onSearch = debounce((searchVal: string) => {
    if (searchVal === '') {
      setIsSearching(false);
    } else {
      setIsSearching(true);
    }
    setSearch(searchVal);
  }, 500);

  useOnClickOutside([inputRef, divRef], () => {
    if (locDomain && value) {
      locDomain.setLocationFormDistance(value.distance);
      setStagedItem(locDomain);
    } else if (!value) {
      setStagedItem(value);
    }
    setIsSearching(false);

    if (isLg && isOpen) {
      onClose();
    }
  });

  const onSelect = (hasClose?: boolean) => (selected: LocDomain) => (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();

    if (useGeoInputType === 'search') {
      setStagedItem(selected);
    }
    setIsSearching(false);
    setSearch('');
    onBlur?.(selected.getLocationForm());
    onChange?.(selected.getLocationForm());
    if (hasClose && (!isLg || useGeoInputType === 'limited')) {
      onClose();
    }
  };

  const onClean = () => {
    setSelectedItem(null);
    setIsSearching(false);
    setStagedItem(null);
    onChange?.(null);
    onBlur?.(null);
    if (isLg) {
      inputRef.current?.focus();
      onOpen();
    }
  };

  const items = locs?.getLocs() ?? null;
  const userItem = current?.getLoc() ?? null;

  return {
    isOpen,
    isSearching,
    isStageAndSelectedEqual,
    isLoading,
    onOpen,
    onClose,
    selectedItem,
    stagedItem,
    locationValue: stagedItem ?? selectedItem,
    userItem,
    items,
    search,
    onSelect,
    onSearch,
    onClean,
    onChangeDistance,
    refs: { inputRef, divRef },
    ...rest,
  };
};

export default useGeoInput;
